"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Lightweight typing effect component
 *
 * Usage:
 * <TextType
 *   as="h1"
 *   texts={["Custom Software Development Services", "Scalable. Secure. High‑Performance."]}
 *   typingSpeed={70}
 *   deletingSpeed={35}
 *   pauseDuration={1600}
 *   loop
 *   showCursor
 *   cursorChar="|"
 *   className="display-5 fw-bold text-white mb-4"
 *   style={{ lineHeight: 1.15 }}
 * />
 */
export default function TextType({
  texts = [],
  as: As = "div",
  typingSpeed = 60,
  deletingSpeed = 30,
  pauseDuration = 1800,
  initialDelay = 0,
  loop = true,
  showCursor = true,
  cursorChar = "|",
  hideCursorOnComplete = true,
  startOnVisible = false,
  className = "",
  style = {},
  ...rest
}) {
  // Stabilize texts across parent re-renders by comparing value instead of reference
  const listKey = useMemo(() => JSON.stringify(texts || [""]), [texts]);
  const list = useMemo(() => (Array.isArray(texts) && texts.length ? texts : [""]), [listKey]);
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [done, setDone] = useState(false);
  const [visible, setVisible] = useState(!startOnVisible);
  const containerRef = useRef(null);
  const timerRef = useRef(null);

  // Start when visible (optional)
  useEffect(() => {
    if (!startOnVisible) return;
    const node = containerRef.current;
    if (!node) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) setVisible(true);
    }, { threshold: 0.1 });
    io.observe(node);
    return () => io.disconnect();
  }, [startOnVisible]);

  // Typing loop
  useEffect(() => {
    if (!visible) return;

    const current = list[idx % list.length];
    const doneTyping = !deleting && text === current;
    const doneDeleting = deleting && text === "";

    let nextDelay = typingSpeed;

    if (doneTyping) {
      if (!loop && idx >= list.length - 1) {
        setDone(true);
        return; // stop here: keep final text, no deletion
      }
      nextDelay = pauseDuration;
      timerRef.current = setTimeout(() => setDeleting(true), nextDelay);
      return () => clearTimeout(timerRef.current);
    }

    if (doneDeleting) {
      const nextIdx = idx + 1;
      if (!loop && nextIdx >= list.length) return;
      timerRef.current = setTimeout(() => {
        setIdx(nextIdx % list.length);
        setDeleting(false);
      }, 250);
      return () => clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      if (deleting) {
        setText((t) => t.slice(0, -1));
      } else {
        const nextChar = current.charAt(text.length);
        setText((t) => t + nextChar);
      }
    }, deleting ? deletingSpeed : (text.length === 0 ? Math.max(typingSpeed, initialDelay) : typingSpeed));

    return () => clearTimeout(timerRef.current);
  }, [visible, list, idx, text, deleting, typingSpeed, deletingSpeed, pauseDuration, loop, initialDelay]);

  return (
    <As ref={containerRef} className={className + " text-type"} style={style} {...rest}>
      <span className="text-type__content">{text}</span>
      {showCursor && (
        <span className={"text-type__cursor" + (done && hideCursorOnComplete ? " text-type__cursor--hidden" : "")}>{cursorChar}</span>
      )}
      <style jsx>{`
        .text-type { /* keep default display (block for h1) so inline children wrap naturally */ }
        .text-type__content { display: inline; }
        .text-type__cursor { display: inline-block; margin-left: 6px; opacity: 1; animation: blink 1s step-end infinite; }
        .text-type__cursor--hidden { opacity: 0; animation: none; }
        @keyframes blink { 50% { opacity: 0; } }
      `}</style>
    </As>
  );
}
